import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type {
  AgentRunRow, ApprovalRow, CheckPillarRow, DataDeskRow, DataFindingRow, DataProjectHealthRow, DepartmentRow,
  AppUsageDayRow, AppUsageFeedRow, AppUsageRow, DeskTicketRow, EventRow, EventTypeRow, FleetComponentRow, FleetUpstreamRow, HeartbeatLatestRow, Json,
  FleetStatusRow, LaneTypeRow, MergeStatusRow, ProjectCheckRow, TicketFeedRow,
} from '../lib/database.types'
import {
  FLEET_HEALTH_SOURCE, WATCHDOG_HEARTBEAT_SOURCE, type FleetHealth,
} from './map/health'
import { lensFilter, payloadPaths, type Lens, type Registry } from './map/eventTypes'
import { AGENT_FINISHED_TYPE } from './map/changelog'
import { DECISION_EVENT_TYPE } from './map/reports'

/**
 * Every read the dashboard makes. There is no write here -- the one mutation
 * goes through the decide_approval function and lives in useDecide.ts.
 *
 * Realtime is not enabled on this project (no table is in the supabase_realtime
 * publication), so these poll. The intervals match what the design claims in the
 * lanes header rather than being as fast as possible: two operators watching a
 * board do not need a second-by-second feed, and the fleet does not move that
 * fast. When realtime is switched on, the transport changes here and the query
 * keys and every consumer stay as they are.
 */
export const POLL = { lanes: 10_000, approvals: 15_000, activity: 20_000 } as const

export const keys = {
  runs: (department: string | null) => ['runs', department ?? '*'] as const,
  events: (limit: number, filter: string | null, paths: string) => ['events', limit, filter, paths] as const,
  eventTypes: ['event-types'] as const,
  changelog: ['changelog'] as const,
  approvals: ['approvals'] as const,
  fleetHealth: ['fleet-health'] as const,
  departments: ['departments'] as const,
  laneTypes: ['lane-types'] as const,
  components: ['components'] as const,
  findings: (department: string) => ['findings', department] as const,
  projectHealth: (department: string) => ['project-health', department] as const,
  dataDesk: ['data-desk'] as const,
  checkPillars: ['check-pillars'] as const,
  projectChecks: (department: string) => ['project-checks', department] as const,
  linearTickets: (department: string) => ['linear-tickets', department] as const,
  linearTicketFeed: (department: string) => ['linear-ticket-feed', department] as const,
  upstream: (department: string) => ['upstream', department] as const,
  appUsage: (department: string) => ['app-usage', department] as const,
  appUsageFeeds: (department: string) => ['app-usage-feeds', department] as const,
  appUsageDays: (app: string, from: string | null) => ['app-usage-days', app, from ?? '*'] as const,
  appUsageDayBounds: (app: string) => ['app-usage-day-bounds', app] as const,
  appUsageAllFeeds: ['app-usage-feeds', '*'] as const,
  fleetStatus: ['fleet-status'] as const,
  mergeStatus: ['merge-status'] as const,
  reportEvents: (types: readonly string[]) => ['report-events', types.join(',')] as const,
  reportDecisions: (runIds: readonly string[]) => ['report-decisions', runIds.join(',')] as const,
}

function unwrap<T>(result: { data: T | null; error: { message: string } | null }): T {
  if (result.error) throw new Error(result.error.message)
  return (result.data ?? []) as T
}

/**
 * The department list: slug, display name and the order the fleet wants them in.
 *
 * Read-only to this app -- `authenticated` holds select on this table and
 * nothing else. It is the only source for which departments exist, so the nav
 * and every filter pill row agree, and so a department that has gone quiet does
 * not fall out of the list the way one derived from `useRuns()` would.
 *
 * It does not poll. Departments change when a lane comes online, which is
 * months apart, not seconds -- so a long staleTime and no interval, unlike the
 * 10/15/20s reads around it. A row added today reaches an open tab on the next
 * reload.
 */
const DEPARTMENTS_STALE_TIME = 60 * 60_000

export function useDepartments() {
  return useQuery({
    queryKey: keys.departments,
    staleTime: DEPARTMENTS_STALE_TIME,
    queryFn: async () =>
      unwrap<DepartmentRow[]>(
        await supabase
          .from('departments')
          .select('slug,display_name,sort_order')
          .order('sort_order', { ascending: true }),
      ),
  })
}

/**
 * The fleet's own inventory: every agent, script and piece of plumbing, with
 * what it reads, what it changes and what it sends. Drives the Directory view.
 *
 * Reference data, and the slowest-moving read in the app. It is published from
 * `98h-agents` by a timer on the box at most hourly, from a descriptor beside
 * each component's code -- so it is read on a cadence that matches what writes
 * it rather than the 10/15/20s the live views use.
 *
 * It does poll, where `useDepartments()` does not, and the reason is the page
 * rather than the data: the Directory states its own freshness, and a tab left
 * open all afternoon claiming a check that stopped happening is the one thing
 * that view has to get right.
 */
const COMPONENTS_STALE_TIME = 30 * 60_000
const COMPONENTS_POLL = 60 * 60_000

/**
 * Which lanes are claimed: the lane registry, published from 98h-agents
 * config/lanes.json by the box at most hourly. Read for one fact, whether a
 * lane's runs are ticket work (see map/tickets.ts `ticketRuns`), so it selects
 * two columns and polls like the departments list -- not at all.
 */
export function useLaneTypes() {
  return useQuery({
    queryKey: keys.laneTypes,
    staleTime: DEPARTMENTS_STALE_TIME,
    queryFn: async () =>
      unwrap<LaneTypeRow[]>(await supabase.from('lane_types').select('lane_type,claimed')),
  })
}

export function useComponents() {
  return useQuery({
    queryKey: keys.components,
    staleTime: COMPONENTS_STALE_TIME,
    refetchInterval: COMPONENTS_POLL,
    queryFn: async () =>
      unwrap<FleetComponentRow[]>(
        await supabase
          .from('fleet_components')
          .select('*')
          .order('kind_sort', { ascending: true })
          .order('sort_order', { ascending: true })
          .order('name', { ascending: true }),
      ),
  })
}

/**
 * The newest RUNS_WINDOW runs: the whole fleet's, or one department's.
 *
 * The window is per department, not shared, when a view is about one
 * department. Every department's runs arrive in one table at very different
 * rates -- the data lane writes one run per project per night, the dev lanes
 * several per ticket -- so a shared window would be sized by whichever writes
 * most, and a quiet department's desk would lose its history to a busy
 * neighbour's. The cross-fleet views (costs, the strip, activity, changelog)
 * still read the shared window, because their question is the fleet's.
 */
export const RUNS_WINDOW = 500

export function useRuns(department: string | null = null) {
  return useQuery({
    queryKey: keys.runs(department),
    refetchInterval: POLL.lanes,
    queryFn: async () => {
      let query = supabase.from('agent_runs').select('*')
      if (department !== null) query = query.eq('department', department)
      return unwrap<AgentRunRow[]>(
        await query.order('created_at', { ascending: false }).limit(RUNS_WINDOW),
      )
    },
  })
}

/**
 * The event-type registry, published from 98h-agents config/event-types.json by
 * the box at most hourly -- the same cadence and the same reasoning as
 * `useComponents()`. Read-only; see map/eventTypes.ts.
 *
 * An empty result is a real state (before the first publish), not an error:
 * the feed then shows raw types and summaries, as it always did.
 */
const EVENT_TYPES_STALE_TIME = 30 * 60_000
const EVENT_TYPES_POLL = 60 * 60_000

export function useEventTypes() {
  return useQuery({
    queryKey: keys.eventTypes,
    staleTime: EVENT_TYPES_STALE_TIME,
    refetchInterval: EVENT_TYPES_POLL,
    queryFn: async () => unwrap<EventTypeRow[]>(await supabase.from('event_types').select('*')),
  })
}

/**
 * The activity feed deliberately does not select `payload` whole. A mirrored
 * Linear webhook body runs to about 3KB, and a row needs a handful of fields:
 * which fields changed, a url, the deploy outcome, and whatever paths the
 * registry's templates name. Pulling whole bodies to render a one-line row
 * would put megabytes over the wire the day the fleet gets busy.
 *
 * The template paths are read off the registry, so a new template that names
 * `{payload.something}` is fetched without a change here. Each becomes an
 * aliased column (`p0:payload->review->>verdict`) and is put back into a small
 * payload object of the same shape, so the renderer reads it like any payload.
 */
export interface ActivityEventRow extends Omit<EventRow, 'payload'> {
  payload: Json
}

const BASE_EVENT_COLUMNS =
  'id,run_id,department,type,summary,ts,' +
  'updatedFrom:payload->updatedFrom,url:payload->>url,pr_url:payload->>pr_url,action:payload->>action'

/** `['review','verdict']` -> `payload->review->>verdict`. */
function jsonPath(path: string[]): string {
  const init = path.slice(0, -1).map((k) => `->${k}`).join('')
  return `payload${init}->>${path[path.length - 1]}`
}

function setPath(target: Record<string, Json>, path: string[], value: Json): void {
  let cursor = target
  for (const key of path.slice(0, -1)) {
    const next = cursor[key]
    if (!next || typeof next !== 'object' || Array.isArray(next)) cursor[key] = {}
    cursor = cursor[key] as Record<string, Json>
  }
  cursor[path[path.length - 1]!] = value
}

/**
 * The feed, newest first and capped.
 *
 * The cap is why the lens filter has to be applied by the database: the 200 is
 * taken before a single row reaches the browser, so a high-frequency type
 * crowds everything else out of the window and no amount of client-side
 * filtering can reach past it. See `lensFilter`.
 *
 * Waits for the registry to settle, so the first fetch uses the right filter
 * rather than fetching twice. A registry that failed to load still settles,
 * and the feed falls back to what it can filter without one.
 *
 * The cap is exported for the same reason `APPROVALS_LIMIT` is: it is the
 * number the arithmetic above turns on, and a view that wants to say something
 * about it should read it rather than repeat it.
 */
export const EVENTS_LIMIT = 200

export function useEvents(registry: Registry, lens: Lens, registryReady: boolean, limit = EVENTS_LIMIT) {
  const filter = lensFilter(registry, lens)
  const paths = payloadPaths(registry)
  return useQuery({
    queryKey: keys.events(limit, filter, paths.map((p) => p.join('.')).join(',')),
    enabled: registryReady,
    refetchInterval: POLL.activity,
    queryFn: async () => {
      const columns = [BASE_EVENT_COLUMNS, ...paths.map((p, i) => `p${i}:${jsonPath(p)}`)].join(',')
      const select = supabase.from('events').select(columns)
      const result = await (filter ? select.or(filter) : select)
        .order('ts', { ascending: false })
        .limit(limit)
      if (result.error) throw new Error(result.error.message)
      const rows = (result.data ?? []) as unknown as Array<Record<string, Json>>
      return rows.map<EventRow>((r) => {
        const payload: Record<string, Json> = {
          updatedFrom: r['updatedFrom'] ?? null,
          url: r['url'] ?? null,
          pr_url: r['pr_url'] ?? null,
          action: r['action'] ?? null,
        }
        paths.forEach((p, i) => {
          const v = r[`p${i}`]
          if (v !== null && v !== undefined) setPath(payload, p, v)
        })
        return {
          id: r['id'] as number,
          run_id: (r['run_id'] as string | null) ?? null,
          department: r['department'] as string,
          type: r['type'] as string,
          summary: (r['summary'] as string | null) ?? null,
          ts: r['ts'] as string,
          payload,
        }
      })
    },
  })
}

/**
 * The changelog wants the whole payload but only of finished runs, which is a
 * handful of rows rather than the whole feed.
 *
 * It needs no lens and never did: the `eq` below is server-side and runs
 * before the limit, so the 60 rows it fetches are 60 finished-agent rows
 * whatever else the table fills up with.
 */
export function useChangelogEvents(limit = 60) {
  return useQuery({
    queryKey: keys.changelog,
    refetchInterval: POLL.activity,
    queryFn: async () =>
      unwrap<EventRow[]>(
        await supabase
          .from('events')
          .select('*')
          .eq('type', AGENT_FINISHED_TYPE)
          .order('ts', { ascending: false })
          .limit(limit),
      ),
  })
}

/** Local midnight, because "overnight" means the operator's night. */
export function startOfLocalDay(now = new Date()): Date {
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)
  return start
}

/**
 * The queue fetches everything still open, plus anything decided since local
 * midnight so a decision stays undoable for the rest of the day.
 *
 * The filter is not cosmetic. Fetching every row and hiding the old ones would
 * mean that once 200 closed rows accumulate -- a nightly data-quality report
 * alone gets there in months -- an older OPEN item drops off the end of the
 * list and is never shown again. That silently breaks the one invariant the
 * runbook names: if a row is in the queue, someone owes it attention.
 */
export const APPROVALS_LIMIT = 200

export function useApprovals() {
  return useQuery({
    queryKey: keys.approvals,
    refetchInterval: POLL.approvals,
    queryFn: async () => {
      const since = startOfLocalDay().toISOString()
      const rows = unwrap<ApprovalRow[]>(
        await supabase
          .from('approvals')
          .select('*')
          .or(`state.eq.pending,decided_at.gte."${since}"`)
          .order('created_at', { ascending: false })
          .limit(APPROVALS_LIMIT),
      )
      return rows
    },
  })
}

/**
 * What the watchdog has filed about the fleet itself, for the line in the cost
 * strip. Two selects, and the second one only when the first came back empty.
 *
 * The findings are the open rows the watchdog filed, oldest first, because the
 * strip shows the oldest and counts the rest. `count: 'exact'` so that count is
 * the number open rather than the number fetched.
 *
 * The second select is the liveness signal, and it is there because an empty
 * result has three meanings, not two. A watchdog that has never run, a fleet
 * with nothing wrong, and a watchdog that died six hours ago all produce
 * exactly the same rows -- none -- and only the middle one is health.
 *
 * So it reads the watchdog's last heartbeat (v_heartbeat_latest): one row, on
 * every poll rather than only in the quiet case, because a finding plus a dead watchdog
 * is a real state too. Findings say what is wrong; the pass says whether
 * anything is still looking.
 *
 * It polls with the queue, because it is a read of the queue: this is the one
 * fact on the page that is worth being a few seconds old rather than a few
 * minutes.
 */
const FLEET_HEALTH_LIMIT = 20

export function useFleetHealth() {
  return useQuery({
    queryKey: keys.fleetHealth,
    refetchInterval: POLL.approvals,
    queryFn: async (): Promise<FleetHealth> => {
      const result = await supabase
        .from('approvals')
        .select('*', { count: 'exact' })
        .eq('source', FLEET_HEALTH_SOURCE)
        .eq('state', 'pending')
        .order('created_at', { ascending: true })
        .limit(FLEET_HEALTH_LIMIT)
      const open = unwrap<ApprovalRow[]>(result)

      // The liveness read, always -- not only when there are no findings.
      // Cheap (one indexed row) and it is what separates "nothing is wrong"
      // from "nothing has told us anything since 10:40".
      const beat = unwrap<Array<Pick<HeartbeatLatestRow, 'ts'>>>(
        await supabase
          .from('v_heartbeat_latest')
          .select('ts')
          .eq('source', WATCHDOG_HEARTBEAT_SOURCE)
          .limit(1),
      )
      return {
        open,
        openCount: result.count ?? open.length,
        lastPassAt: beat[0]?.ts ?? null,
      }
    },
  })
}

/**
 * Every event for one run, oldest first: the run trace drawer's step list.
 * Fetched only while the drawer is open, and with the payload, because a step's
 * detail is in there.
 */
export function useRunEvents(runId: string | null) {
  return useQuery({
    queryKey: ['run-events', runId],
    enabled: runId !== null,
    queryFn: async () =>
      unwrap<EventRow[]>(
        await supabase
          .from('events')
          .select('*')
          .eq('run_id', runId as string)
          .order('ts', { ascending: true })
          .limit(200),
      ),
  })
}

/**
 * A department's data findings: every open one, and the ones cleared recently
 * enough to be worth seeing go. One row per problem a project's nightly checks
 * found (98h-infra/sql/fleet/20260923010000_data_findings.sql), read through
 * `v_findings` (20260925050000_findings.sql renamed the table from
 * `data_findings`) so the decision on each one's queue item comes with it.
 *
 * Polled slowly, on the cadence of what writes it. A finding changes when a
 * project reports -- nightly -- and when the ticketer files or closes its
 * ticket, half an hour after that. Five minutes is already far more often than
 * either; a tab left open overnight still shows the morning's state by the
 * time anyone looks.
 *
 * Keyed and filtered by department rather than naming one: the desk is generic,
 * and a department with no findings gets an empty list, not an error.
 */
export const FINDINGS_CLEARED_DAYS = 30
const FINDINGS_POLL = 5 * 60_000

export function useDataFindings(department: string) {
  return useQuery({
    queryKey: keys.findings(department),
    refetchInterval: FINDINGS_POLL,
    enabled: department !== '',
    queryFn: async () => {
      const since = new Date(Date.now() - FINDINGS_CLEARED_DAYS * 24 * 60 * 60_000).toISOString()
      return unwrap<DataFindingRow[]>(
        await supabase
          .from('v_findings')
          .select('*')
          .eq('department', department)
          .or(`status.eq.open,cleared_at.gte.${since}`)
          .order('first_seen_at', { ascending: true })
          .limit(500),
      )
    },
  })
}

/** Each reporting project's latest summary and when it last reported. Same cadence as findings. */
export function useDataProjectHealth(department: string) {
  return useQuery({
    queryKey: keys.projectHealth(department),
    refetchInterval: FINDINGS_POLL,
    enabled: department !== '',
    queryFn: async () =>
      unwrap<DataProjectHealthRow[]>(
        await supabase
          .from('data_project_health')
          .select('*')
          .eq('department', department)
          .order('project', { ascending: true }),
      ),
  })
}

/**
 * The per-pillar standing of every reporting project: one row each, with a
 * status, an active check count and an open-findings count for access,
 * freshness, shape and agreement, plus whether it matches its contract
 * (98h-infra sql/fleet, `v_data_desk`, security invoker).
 *
 * Every rule is the view's. It applies the ops contract -- a status is the one
 * the project reported where it reports one, and derived from open findings
 * where it does not -- so nothing downstream of here recomputes or second-guesses
 * a rating; `map/pillars.ts` only lays the columns out.
 *
 * It carries no department column and is not filtered by one. The view is one
 * row per project that reports, and which of those belong to a desk is settled
 * by the department's own `data_project_health` rows, which the desk already
 * reads -- so the scoping stays where the department filter already is rather
 * than becoming a hardcoded slug here (product rule 2).
 *
 * Polled on the cadence of what writes it, like the findings beside it: a row
 * changes when a project reports, which is nightly.
 */
export function useDataDesk() {
  return useQuery({
    queryKey: keys.dataDesk,
    refetchInterval: FINDINGS_POLL,
    queryFn: async () => unwrap<DataDeskRow[]>(await supabase.from('v_data_desk').select('*')),
  })
}

/**
 * Which pillars exist, in the order the fleet wants them, and what to call one.
 *
 * Reference data published from 98h-agents by the box, like the departments
 * list and the event-type registry -- so it is read the way they are, with a
 * long staleTime and no interval. A pillar is added by a person merging a
 * change to the registry, not by a run.
 *
 * Ordered by `sort_order`, which is the order the cells are rendered in. The
 * mapper holds it too, because that order is the registry's fact rather than
 * this query's. A retired pillar is still fetched: the desk view publishes the
 * columns it publishes, and dropping a row here would hide one that still has
 * a column behind it.
 */
export function useCheckPillars() {
  return useQuery({
    queryKey: keys.checkPillars,
    staleTime: DEPARTMENTS_STALE_TIME,
    queryFn: async () =>
      unwrap<CheckPillarRow[]>(
        await supabase
          .from('check_pillars')
          .select('slug,display_name,question,sort_order')
          .order('sort_order', { ascending: true }),
      ),
  })
}

/**
 * Every check a department's projects report, reduced to the one fact the
 * drill-down needs: which pillar it belongs to.
 *
 * Three columns rather than `*`. The table carries a description, a source
 * query and timestamps that the findings themselves already carry, and this
 * read exists only to turn a finding's `(project_ref, check_name)` into a
 * pillar.
 *
 * Filtered by the desk's slug, like the findings and the health rows beside it,
 * so a department is narrowed by the page rather than named here.
 */
export function useProjectChecks(department: string) {
  return useQuery({
    queryKey: keys.projectChecks(department),
    refetchInterval: FINDINGS_POLL,
    enabled: department !== '',
    queryFn: async () =>
      unwrap<ProjectCheckRow[]>(
        await supabase
          .from('project_checks')
          .select('project_ref,check_name,pillar')
          .eq('department', department)
          .limit(1000),
      ),
  })
}

/**
 * A department's open Linear tickets, as the watchdog last published them
 * (fleet_tickets, through v_desk_tickets). The watchdog replaces the list every ten
 * minutes, so a shorter poll would show nothing new.
 */
const TICKETS_POLL = 5 * 60_000

export function useLinearTickets(department: string) {
  return useQuery({
    queryKey: keys.linearTickets(department),
    refetchInterval: TICKETS_POLL,
    enabled: department !== '',
    queryFn: async () =>
      unwrap<DeskTicketRow[]>(
        await supabase.from('v_desk_tickets').select('*').eq('department', department).limit(500),
      ),
  })
}

/** When the department's ticket list was last published, and how many it held. Zero or one row. */
export function useLinearTicketFeed(department: string) {
  return useQuery({
    queryKey: keys.linearTicketFeed(department),
    refetchInterval: TICKETS_POLL,
    enabled: department !== '',
    queryFn: async () =>
      unwrap<TicketFeedRow[]>(
        await supabase.from('fleet_ticket_feeds').select('*').eq('department', department),
      ),
  })
}

/**
 * A department's Linear work that has not started yet, as the fleet last counted
 * it: one row per workflow state, replaced whole (`fleet_upstream`).
 *
 * Published by the same watchdog pass as the ticket list, so it reads on the same
 * cadence; a shorter poll would show the same snapshot again. Every row is
 * fetched, kept and dropped alike, because which state types mean "waiting" is a
 * presentation decision and it belongs in the mapper with the reason written
 * beside it, not in a filter string here.
 */
export function useUpstream(department: string) {
  return useQuery({
    queryKey: keys.upstream(department),
    refetchInterval: TICKETS_POLL,
    enabled: department !== '',
    queryFn: async () =>
      unwrap<FleetUpstreamRow[]>(
        await supabase.from('fleet_upstream').select('*').eq('department', department),
      ),
  })
}

/**
 * A department's apps' usage: per person and page over the last thirty days
 * (`v_desk_app_usage`), and when each app last reported (`app_usage_feeds`).
 *
 * The sender posts hourly, so a poll faster than a quarter hour would show the
 * same copy again. Every row is read, a page at a time: the API caps one
 * response at its max-rows setting (1000 by default) without an error, and a
 * cut-off list would understate someone's use without saying so. The order
 * covers every column the view groups by (a name or role change inside the
 * window makes a second row for that person and page), so it is total and no
 * row falls between two pages. Paging advances by what came back and stops on
 * an empty page, so a cap below USAGE_PAGE cannot end it early.
 */
const USAGE_POLL = 15 * 60_000
const USAGE_PAGE = 1000

export function useAppUsage(department: string) {
  return useQuery({
    queryKey: keys.appUsage(department),
    refetchInterval: USAGE_POLL,
    enabled: department !== '',
    queryFn: async () => {
      const rows: AppUsageRow[] = []
      for (;;) {
        const page = unwrap<AppUsageRow[]>(
          await supabase
            .from('v_desk_app_usage')
            .select('*')
            .eq('department', department)
            .order('app')
            .order('user_id')
            .order('full_name')
            .order('role')
            .order('path')
            .range(rows.length, rows.length + USAGE_PAGE - 1),
        )
        if (page.length === 0) return rows
        rows.push(...page)
      }
    },
  })
}

export function useAppUsageFeeds(department: string) {
  return useQuery({
    queryKey: keys.appUsageFeeds(department),
    refetchInterval: USAGE_POLL,
    enabled: department !== '',
    queryFn: async () =>
      unwrap<AppUsageFeedRow[]>(
        await supabase.from('app_usage_feeds').select('*').eq('department', department),
      ),
  })
}

/**
 * One app's days of page views, a row per day, person and page (`app_usage`),
 * from `from` onwards -- or its whole history when `from` is null.
 *
 * The desk reads `v_desk_app_usage`, which sums a fixed thirty days and has no
 * day column; the reports need the days themselves, so this reads the table.
 * Both filters are the server's work rather than the mapper's, because the
 * point of them is the rows that never cross the wire: the table gains a day
 * of rows every day for every app that reports, and a page that read all of it
 * to draw the last month would get slower every week for nothing.
 *
 * Paged exactly as the desk's query is, and for the same reason: the API caps
 * one response at its max-rows setting (1000 by default) without an error, and
 * USTApp alone is already near five thousand rows, so a single request would
 * return a truncated history that looks like a quiet month. The order covers
 * every column the table is unique on, so it is total and no row falls between
 * two pages; paging advances by what came back and stops on an empty page, so
 * a cap below USAGE_PAGE cannot end it early.
 *
 * No app and no department is named here. Which apps can be reported on comes
 * from the feed rows and which one to read comes from the URL, so an app the
 * fleet starts reporting appears with no code change (product rule 2). Nothing
 * is asked for until one is picked.
 */
export function useAppUsageDays(app: string, from: string | null) {
  return useQuery({
    queryKey: keys.appUsageDays(app, from),
    refetchInterval: USAGE_POLL,
    enabled: app !== '',
    queryFn: async () => {
      const rows: AppUsageDayRow[] = []
      for (;;) {
        // Built inside the loop: a builder is spent by the request it makes.
        const mine = supabase.from('app_usage').select('*').eq('app', app)
        const page = unwrap<AppUsageDayRow[]>(
          await (from ? mine.gte('day', from) : mine)
            .order('app')
            .order('day')
            .order('user_id')
            .order('path')
            .range(rows.length, rows.length + USAGE_PAGE - 1),
        )
        if (page.length === 0) return rows
        rows.push(...page)
      }
    },
  })
}

/** The ends of one app's recorded history: two days, or two nulls if it has none. */
export interface AppUsageDayBounds {
  firstDay: string | null
  lastDay: string | null
}

/**
 * The first and last day an app has ever recorded, as two one-row reads.
 *
 * The page states where an app's history begins and counts its windows back
 * from where the history ends, and both of those are facts about rows the
 * window itself leaves out -- which is the whole reason the day query can stop
 * fetching them. Ordered and limited to one row at each end rather than
 * summed, so this stays two tiny requests however many days the table holds
 * and does not depend on aggregate functions being exposed by the API.
 */
export function useAppUsageDayBounds(app: string) {
  return useQuery({
    queryKey: keys.appUsageDayBounds(app),
    refetchInterval: USAGE_POLL,
    enabled: app !== '',
    queryFn: async (): Promise<AppUsageDayBounds> => {
      const edge = (ascending: boolean) =>
        supabase.from('app_usage').select('day').eq('app', app).order('day', { ascending }).limit(1)
      const [first, last] = await Promise.all([edge(true), edge(false)])
      return {
        firstDay: unwrap<{ day: string }[]>(first)[0]?.day ?? null,
        lastDay: unwrap<{ day: string }[]>(last)[0]?.day ?? null,
      }
    },
  })
}

/** Every app's last report, across departments: the list of apps a report can be about. */
export function useAllAppUsageFeeds() {
  return useQuery({
    queryKey: keys.appUsageAllFeeds,
    refetchInterval: USAGE_POLL,
    queryFn: async () => unwrap<AppUsageFeedRow[]>(await supabase.from('app_usage_feeds').select('*')),
  })
}

/**
 * The fleet's own standing: one row per job, as the `fleet-status` agent last
 * published it.
 *
 * The whole panel is this one read and nothing else. Every figure it shows --
 * the counts, the queue's standing, how fresh the page is -- comes out of
 * these rows, so opening the Status view makes no live call to GitHub, Linear
 * or Claude to find out whether a job is healthy. The agent asked those
 * questions on its own timer and wrote down the answers; this reads the
 * answers.
 *
 * It does poll, at a third of the cadence that writes it. Not for the rows,
 * which change every fifteen minutes at most, but for the staleness banner:
 * that banner is a claim about *now*, and a tab left open since this morning
 * would otherwise go on reporting a pass from this morning as current, which
 * is the exact failure the banner exists to catch, reintroduced one level up.
 */
export const STATUS_POLL = 5 * 60_000

export function useFleetStatus() {
  return useQuery({
    queryKey: keys.fleetStatus,
    refetchInterval: STATUS_POLL,
    queryFn: async () => unwrap<FleetStatusRow[]>(await supabase.from('fleet_status').select('*')),
  })
}

/**
 * Which open pull requests can be merged, as the `merge-status` job last
 * published them -- and the backlog tickets whose blockers are all done.
 *
 * The whole Merge view is this one read. Every verdict, reason and post-merge
 * step it shows was worked out on the box against GitHub and Linear and
 * written down, so opening the view calls neither: the judgement is already
 * made and this reads it, the same rule the Status panel holds.
 *
 * One pass at a time, written every fifteen minutes, so the rows barely move.
 * It polls at the status cadence for the staleness banner rather than for the
 * rows -- that banner is a claim about *now*, and a tab left open since this
 * morning would otherwise go on offering this morning's answer about what is
 * safe to merge.
 */
export function useMergeStatus() {
  return useQuery({
    queryKey: keys.mergeStatus,
    refetchInterval: STATUS_POLL,
    queryFn: async () => unwrap<MergeStatusRow[]>(await supabase.from('merge_status').select('*')),
  })
}

/**
 * Every event about a report the fleet has generated: the generations
 * themselves and the lane's later signals about them.
 *
 * Which types those are is not a list here. The caller derives them from the
 * event-type registry -- the types whose sentence names both the report and the
 * period, see `map/reports.ts` `reportEventTypes` -- so a report type the fleet
 * registers next is read with no change in this file (product rule 2). Nothing
 * is asked for until the registry has settled, the way the feed waits for it.
 *
 * It selects the whole payload, where the feed deliberately does not. These are
 * the lane's own small objects rather than mirrored webhook bodies, and a row
 * needs several keys out of each -- the link, the report, the period and
 * whatever the registry's template names.
 *
 * Polled at the status cadence rather than the feed's: a report is generated
 * weekly and decided minutes later, and this list is the record of that rather
 * than the surface anyone works from. The Inbox is, at 15s.
 */
const REPORT_EVENTS_LIMIT = 200

export function useReportEvents(types: readonly string[]) {
  return useQuery({
    queryKey: keys.reportEvents(types),
    enabled: types.length > 0,
    refetchInterval: STATUS_POLL,
    queryFn: async () =>
      unwrap<EventRow[]>(
        await supabase
          .from('events')
          .select('*')
          .in('type', types as string[])
          .order('ts', { ascending: false })
          .limit(REPORT_EVENTS_LIMIT),
      ),
  })
}

/** What was decided about a set of runs: the decision events, and the queue rows themselves. */
export interface ReportDecisions {
  events: EventRow[]
  approvals: ApprovalRow[]
}

/**
 * The decisions behind a set of generated reports, from both places one is
 * recorded.
 *
 * Narrowed by run rather than by type, which is the whole reason it is a second
 * read. `approval.decided` is written for every closed approval in the fleet,
 * so fetching it by type alone would spend the window on dev reviews and return
 * none of the handful that are about a report.
 *
 * Two selects, because a turn-down is in two shapes and only one of them is an
 * event: five of the decided rows carry no `approval.decided` at all, including
 * the w/e 2026-10-24 dismissal. Which queue rows are about a report is the
 * mapper's call, read off the `report` block in the context rather than off a
 * source string, so this fetches the runs' rows and lets the structure decide.
 */
export function useReportDecisions(runIds: readonly string[]) {
  return useQuery({
    queryKey: keys.reportDecisions(runIds),
    enabled: runIds.length > 0,
    refetchInterval: STATUS_POLL,
    queryFn: async (): Promise<ReportDecisions> => {
      const ids = runIds as string[]
      const [events, approvals] = await Promise.all([
        supabase
          .from('events')
          .select('*')
          .eq('type', DECISION_EVENT_TYPE)
          .in('run_id', ids)
          .order('ts', { ascending: false })
          .limit(REPORT_EVENTS_LIMIT),
        supabase.from('approvals').select('*').in('run_id', ids).limit(REPORT_EVENTS_LIMIT),
      ])
      return {
        events: unwrap<EventRow[]>(events),
        approvals: unwrap<ApprovalRow[]>(approvals),
      }
    },
  })
}
